import { useEffect, useRef, useState } from "react";
import { motion, useInView } from "framer-motion";
import { ArrowsHorizontal } from "@phosphor-icons/react";

export const EASE = [0.16, 1, 0.3, 1];

export const MermaidDiagram = ({ code, testId = "article-diagram" }) => {
  const [svg, setSvg] = useState("");
  const [failed, setFailed] = useState(false);
  const idRef = useRef(`mmd-${Math.random().toString(36).slice(2)}`);
  const wrapRef = useRef(null);
  const scrollRef = useRef(null);
  const inView = useInView(wrapRef, { once: true, margin: "-120px" });

  useEffect(() => {
    if (!svg) return;
    const el = scrollRef.current;
    if (!el) return;
    const center = () => {
      if (el.scrollWidth > el.clientWidth) {
        el.scrollLeft = (el.scrollWidth - el.clientWidth) / 2;
      }
    };
    center();
    const t1 = setTimeout(center, 300);
    const t2 = setTimeout(center, 1200);
    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
    };
  }, [svg]);

  useEffect(() => {
    let cancelled = false;
    const render = async () => {
      try {
        const mermaid = (await import("mermaid")).default;
        mermaid.initialize({
          startOnLoad: false,
          securityLevel: "strict",
          theme: "dark",
          themeVariables: {
            background: "#0A0A0A",
            primaryColor: "#141414",
            primaryTextColor: "#FAFAFA",
            primaryBorderColor: "rgba(230, 228, 217, 0.35)",
            lineColor: "#8a8a8a",
            secondaryColor: "#101010",
            tertiaryColor: "#0A0A0A",
            fontFamily: "Manrope, sans-serif",
            fontSize: "14px",
          },
          flowchart: { curve: "basis", htmlLabels: true },
        });
        const result = await mermaid.render(idRef.current, code);
        if (!cancelled) setSvg(result.svg);
      } catch (e) {
        if (!cancelled) setFailed(true);
      }
    };
    render();
    return () => {
      cancelled = true;
    };
  }, [code]);

  const reduced =
    typeof window !== "undefined" &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches;

  if (failed) return null;
  return (
    <motion.div
      ref={wrapRef}
      data-testid={testId}
      initial={reduced ? false : { opacity: 0, y: 32 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.9, ease: EASE }}
      className="my-12 border border-white/10 bg-white/[0.02] p-4 sm:p-6 lg:p-10"
    >
      <p className="sm:hidden flex items-center gap-2 font-mono text-[9px] uppercase tracking-[0.2em] text-white/30 mb-4">
        <ArrowsHorizontal size={12} />
        Swipe to explore the diagram
      </p>
      <div
        ref={scrollRef}
        className="overflow-x-auto [&_svg]:min-w-[600px] [&_svg]:max-w-full [&_svg]:h-auto"
        dangerouslySetInnerHTML={{ __html: svg }}
      />
    </motion.div>
  );
};
